import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from '@/lib/api/client';
import type { SurveysSearch } from './search';

export type Survey = Schemas['SurveyDto'];
export type SurveyQuestion = Schemas['SurveyQuestionDto'];
export type SurveySummary = Schemas['SurveyResponseSummaryDto'];
export type QuestionSummary = Schemas['QuestionSummaryDto'];
export type SurveyResponse = Schemas['SurveyResponseItemDto'];
export type CreateSurveyInput = Schemas['CreateSurveyRequest'];
export type UpdateSurveyInput = Schemas['UpdateSurveyRequest'];

export const SURVEYS_PAGE_SIZE = 25;
export const RESPONSES_PAGE_SIZE = 20;

export const surveyKeys = {
  all: ['surveys'] as const,
  list: (search: SurveysSearch) => [...surveyKeys.all, 'list', search] as const,
  deletedCount: () => [...surveyKeys.all, 'deleted-count'] as const,
  detail: (id: string) => [...surveyKeys.all, 'detail', id] as const,
  summary: (id: string) => [...surveyKeys.all, 'summary', id] as const,
  responses: (id: string, page: number) => [...surveyKeys.all, 'responses', id, page] as const,
};

/** Surveys newest first; `deleted` lists the ones kept because runners saw or answered them. */
export const surveysQuery = (search: SurveysSearch) =>
  queryOptions({
    queryKey: surveyKeys.list(search),
    queryFn: () =>
      unwrap(
        api.GET('/api/admin/surveys', {
          params: {
            query: { page: search.page, pageSize: SURVEYS_PAGE_SIZE, deleted: !!search.deleted },
          },
        }),
      ),
    placeholderData: keepPreviousData,
  });

export const deletedSurveyCountQuery = () =>
  queryOptions({
    queryKey: surveyKeys.deletedCount(),
    queryFn: () =>
      unwrap(
        api.GET('/api/admin/surveys', { params: { query: { pageSize: 1, deleted: true } } }),
      ).then((page) => page.totalCount),
  });

export const surveyQuery = (id: string) =>
  queryOptions({
    queryKey: surveyKeys.detail(id),
    queryFn: () =>
      unwrap(api.GET('/api/admin/surveys/{surveyId}', { params: { path: { surveyId: id } } })),
  });

export const surveySummaryQuery = (id: string) =>
  queryOptions({
    queryKey: surveyKeys.summary(id),
    queryFn: () =>
      unwrap(
        api.GET('/api/admin/surveys/{surveyId}/summary', { params: { path: { surveyId: id } } }),
      ),
  });

/** The same summary, read fresh for the delete confirm under its own key so a failed read leaves the Summary tab alone. */
export const surveyReachQuery = (id: string) =>
  queryOptions({
    queryKey: [...surveyKeys.summary(id), 'reach'] as const,
    queryFn: () =>
      unwrap(
        api.GET('/api/admin/surveys/{surveyId}/summary', { params: { path: { surveyId: id } } }),
      ),
    staleTime: 0,
  });

/** One page of responses, newest first, each answer naming its question. */
export const surveyResponsesQuery = (id: string, page: number) =>
  queryOptions({
    queryKey: surveyKeys.responses(id, page),
    queryFn: () =>
      unwrap(
        api.GET('/api/admin/surveys/{surveyId}/responses', {
          params: { path: { surveyId: id }, query: { page, pageSize: RESPONSES_PAGE_SIZE } },
        }),
      ),
    placeholderData: keepPreviousData,
  });

function useRefresh() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: surveyKeys.all });
}

export function useCreateSurvey() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (input: CreateSurveyInput) =>
      unwrap(api.POST('/api/admin/surveys', { body: input })),
    onSettled: refresh,
  });
}

export function useUpdateSurvey() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateSurveyInput }) =>
      unwrap(
        api.PUT('/api/admin/surveys/{surveyId}', {
          params: { path: { surveyId: id } },
          body: input,
        }),
      ),
    onSettled: refresh,
  });
}

export function useDeleteSurvey() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/api/admin/surveys/{surveyId}', { params: { path: { surveyId: id } } })),
    onSettled: refresh,
  });
}
